import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Progress } from 'antd';
import { CheckCircleFilled, LockOutlined } from '@ant-design/icons';

import { Tag, plural } from '../ds';
import { newsApi, type NewsPollDto } from '../../lib/wikiApi';

/**
 * Опрос внутри новости — отрисовка + голосование. Логика 1-в-1 с CRM
 * (frontend/src/pages/news/NewsPollSection.tsx): варианты сразу кликабельны,
 * single-choice заменяет голос, multi-choice тоглит. Голос == подтверждение
 * ознакомления (backend пишет NewsRead сам). Закрытый опрос — только
 * результаты. Голосует авторизованный вики-вьюер (тот, кто видит новость).
 */
export function NewsPoll({ newsId, poll }: { newsId: string; poll: NewsPollDto }): JSX.Element {
  const qc = useQueryClient();
  // Локальная копия для optimistic-обновления (лента кэшируется одним списком,
  // поэтому держим состояние опроса в стейте компонента, а на успех — синкаем).
  const [local, setLocal] = useState<NewsPollDto>(poll);
  const isClosed = !!local.closedAt;
  const totalVotes = useMemo(() => local.options.reduce((s, o) => s + o.voteCount, 0), [local.options]);

  const voteMutation = useMutation({
    mutationFn: (optionIds: string[]) => newsApi.votePoll(newsId, optionIds),
    onMutate: (optionIds): void => {
      // Optimistic: пересчёт счётчиков по дельте выбора.
      setLocal((prev) => {
        const was = new Set(prev.myVoteOptionIds);
        const now = new Set(optionIds);
        const options = prev.options.map((o) => {
          const had = was.has(o.id);
          const has = now.has(o.id);
          if (had && !has) return { ...o, voteCount: Math.max(0, o.voteCount - 1) };
          if (!had && has) return { ...o, voteCount: o.voteCount + 1 };
          return o;
        });
        const becameVoter = was.size === 0 && now.size > 0;
        const leftVoter = was.size > 0 && now.size === 0;
        return {
          ...prev,
          myVoteOptionIds: optionIds,
          options,
          totalVoters: prev.totalVoters + (becameVoter ? 1 : 0) - (leftVoter ? 1 : 0),
        };
      });
    },
    onSuccess: (fresh) => {
      setLocal(fresh);
      void qc.invalidateQueries({ queryKey: ['wiki', 'news', 'list'] });
    },
    onError: () => {
      // Откат к серверной правде из исходного prop'а ленты.
      setLocal(poll);
    },
  });

  const handleToggle = (optionId: string): void => {
    if (isClosed || voteMutation.isPending) return;
    if (local.isMultiple) {
      const set = new Set(local.myVoteOptionIds);
      if (set.has(optionId)) set.delete(optionId);
      else set.add(optionId);
      voteMutation.mutate([...set]);
    } else {
      if (local.myVoteOptionIds.length === 1 && local.myVoteOptionIds[0] === optionId) return;
      voteMutation.mutate([optionId]);
    }
  };

  const hasMyVote = local.myVoteOptionIds.length > 0;

  return (
    <div style={{ marginTop: 18, padding: 16, border: '1px solid var(--fk-border)', borderRadius: 'var(--fk-radius-md)', background: 'var(--fk-bg-subtle)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <Tag color="indigo">Опрос · голос = подтверждение</Tag>
        {local.isMultiple && <Tag>Множественный выбор</Tag>}
        {local.isAnonymous && <Tag>Анонимный</Tag>}
        {isClosed && <Tag color="red" icon={<LockOutlined />}>Закрыт</Tag>}
        <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', marginLeft: 'auto' }}>
          {local.totalVoters} {plural(local.totalVoters, 'голос', 'голоса', 'голосов')}
        </span>
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--fk-indigo)', marginBottom: 12 }}>{local.question}</div>
      {isClosed && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Опрос закрыт" description="Голосовать больше нельзя — ниже финальные результаты." />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {local.options.map((o) => {
          const pct = totalVotes > 0 ? Math.round((o.voteCount * 100) / totalVotes) : 0;
          const isMine = local.myVoteOptionIds.includes(o.id);
          const clickable = !isClosed && !voteMutation.isPending;
          return (
            <button
              key={o.id}
              type="button"
              disabled={!clickable}
              onClick={() => handleToggle(o.id)}
              style={{
                textAlign: 'left', fontFamily: 'inherit', width: '100%', padding: 10, borderRadius: 6,
                background: isMine ? 'var(--fk-orange-soft)' : 'var(--fk-surface)',
                border: isMine ? '2px solid var(--fk-orange)' : '1px solid var(--fk-border)',
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontWeight: isMine ? 600 : 500, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isMine ? (
                    <CheckCircleFilled style={{ color: 'var(--fk-orange)', fontSize: 16 }} />
                  ) : (
                    <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: local.isMultiple ? 4 : '50%', border: '2px solid var(--fk-border-strong)' }} />
                  )}
                  {o.text}
                </span>
                <span style={{ fontSize: 12, color: 'var(--fk-text-muted)', fontWeight: isMine ? 600 : 400 }}>{o.voteCount} ({pct}%)</span>
              </div>
              <Progress percent={pct} showInfo={false} size="small" strokeColor={isMine ? 'var(--fk-orange)' : 'var(--fk-indigo)'} style={{ marginBottom: 0 }} />
            </button>
          );
        })}
      </div>
      {!isClosed && hasMyVote && (
        <button
          type="button"
          disabled={voteMutation.isPending}
          onClick={() => voteMutation.mutate([])}
          style={{ marginTop: 10, background: 'transparent', border: 'none', color: 'var(--fk-error)', cursor: 'pointer', fontSize: 12.5, fontFamily: 'inherit', padding: 0 }}
        >
          Отозвать голос
        </button>
      )}
    </div>
  );
}
